import {z} from 'zod';

export const UuidV4Schema = z.uuidv4();
export const IsoUtcDateTimeSchema = z.iso.datetime({offset: false});

export const ApiErrorSchema = z.strictObject({
  code: z.string().min(1),
  message: z.string().min(1),
  requestId: UuidV4Schema,
  fieldErrors: z.record(z.string(), z.array(z.string())).nullable(),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;

export const CursorPageSchema = <T extends z.ZodType>(itemSchema: T) =>
  z.strictObject({
    items: z.array(itemSchema),
    nextCursor: z.string().min(1).nullable(),
  });

export type CursorPage<T> = {
  items: T[];
  nextCursor: string | null;
};

// ---------------------------------------------------------------------------
// Vocabulário compartilhado entre métrica e painel
// ---------------------------------------------------------------------------

/**
 * Os dois nomes abaixo moram aqui, e não em `metrics.ts`, por uma razão de
 * carregamento: `panel.ts` precisa dos dois para descrever a tela de Métricas,
 * e `metrics.ts` já importa `panel.ts` para o nível de autonomia. Deixá-los lá
 * fecharia um ciclo entre os dois módulos, e ciclo de módulo com `z.enum` no
 * corpo não quebra no `import`: ele quebra depois, com um schema `undefined`
 * dentro de outro, num erro que não aponta para a causa.
 */

/**
 * As métricas que a coleta conhece. É lista de PERMISSÃO: nome remoto que não
 * estiver mapeado para um destes não vira snapshot, ele é registrado como
 * ignorado. Lista de bloqueio deixaria uma métrica nova da Meta entrar calada,
 * com semântica que ninguém conferiu.
 */
export const METRIC_NAMES = [
  'reach',
  'views',
  'profileVisits',
  'followers',
  'saved',
  'shares',
  'watchTimeMs',
  'avgWatchTimeMs',
  'videoDurationMs',
] as const;

export const MetricNameSchema = z.enum(METRIC_NAMES);
export type MetricName = z.infer<typeof MetricNameSchema>;

/**
 * Os formatos de peça que esta esteira publica.
 *
 * A lista é assunto de MÉTRICA antes de ser assunto de grade: é ela que decide
 * o plano de consulta da Meta. Um nome próprio de Reels derruba a chamada
 * inteira de uma Story com o código 100 da Graph, e junto com ela morrem as
 * métricas que a Story tinha. Foi esse o achado F13.
 *
 * `carousel` já está na lista antes de existir peça de carrossel. É de
 * propósito: o lote 11 acrescenta o formato ao editorial, e o que ele precisa
 * cumprir para as métricas do carrossel aparecerem é gravar `carousel` em
 * `content_pieces.kind` e preencher `publishing_jobs.remote_media_id`. Nenhum
 * arquivo de métrica precisa mudar. Um formato que a coleta não conhece NÃO
 * vira `reel` por omissão: a peça sai da rotina com motivo declarado.
 */
export const MEDIA_FORMATS = ['reel', 'story', 'photo', 'carousel'] as const;

export const MediaFormatSchema = z.enum(MEDIA_FORMATS);
export type MediaFormat = z.infer<typeof MediaFormatSchema>;
